import { Pencil1Icon, ResetIcon, TrashIcon } from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";

import {
  IIntegrationApiKey,
  TIntegrationApiKeyState,
} from "../../../@types/admin/AdminIntegrationApiKey";
import { TableSorted } from "../../../components/TableSorted";
import { useUpdateIntegrationApiKey } from "../../../hooks/mutations/admin/integrationApiKeys";
import {
  INTEGRATION_API_KEYS_QUERY_KEY,
  useIntegrationApiKeys,
} from "../../../hooks/queries/admin/integrationApiKeys";
import { useModal } from "../../../hooks/useModal";
import { useToast } from "../../../hooks/useToast";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import { errorToastMessage } from "../../../utils/HttpError";
import {
  AdminSelectField,
  ErrorMessage,
  FilterButton,
  FilterGroup,
  FilterLabel,
  FiltersActions,
  FiltersContainer,
  PageContainer,
  PageHeader,
  PageInfoTooltip,
  PageTitle,
  PageTitleGroup,
  RowAction,
  RowActionGroup,
  SearchInput,
  StatusBadge,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
  TruncatedCell,
  useMockPagination,
} from "../shared";
import {
  ChipList,
  ClaimChip,
  ExpiryCell,
  KeyPrefix,
} from "./AdminIntegrationApiKeys.styles";
import { IntegrationApiKeyModal } from "./IntegrationApiKeyModal";
import { RevokeIntegrationApiKeyModal } from "./RevokeIntegrationApiKeyModal";

type TStateFilter = "" | TIntegrationApiKeyState;

const STATE_OPTIONS: { value: TStateFilter; label: string }[] = [
  { value: "", label: "All" },
  { value: "Active", label: "Active" },
  { value: "Revoked", label: "Revoked" },
  { value: "Expired", label: "Expired" },
];

/** A key row with the derived bits the search and filter read. */
interface IApiKeyRow extends IIntegrationApiKey {
  state: TIntegrationApiKeyState;
  /** Claims joined for the client-side search. */
  claimsText: string;
}

/** Expired wins over revoked: an expired key cannot be brought back by reactivating it. */
export function apiKeyState(
  apiKey: IIntegrationApiKey
): TIntegrationApiKeyState {
  if (apiKey.isExpired) return "Expired";
  return apiKey.active ? "Active" : "Revoked";
}

export function stateTone(
  state: TIntegrationApiKeyState
): "active" | "inactive" | "error" {
  if (state === "Active") return "active";
  if (state === "Expired") return "error";
  return "inactive";
}

const SEARCH_FIELDS: (keyof IApiKeyRow)[] = [
  "keyPrefix",
  "description",
  "claimsText",
  "region",
];

/**
 * Integration API keys — the machine credentials the integration API, Retool
 * and the webhooks authenticate with. The page only ever sees key metadata:
 * the value is shown once, at creation, inside the create modal.
 */
export function AdminIntegrationApiKeys() {
  document.title = "API keys - Admin";

  const { openModal, closeModal } = useModal();
  const { showToast } = useToast();
  const { apiKeys, isLoading, isError } = useIntegrationApiKeys();
  const { updateApiKey, isUpdatingApiKey } = useUpdateIntegrationApiKey();
  const [stateFilter, setStateFilter] = useState<TStateFilter>("");

  const rows: IApiKeyRow[] = useMemo(
    () =>
      apiKeys
        .map((apiKey) => ({
          ...apiKey,
          state: apiKeyState(apiKey),
          claimsText: apiKey.claims.join(" "),
        }))
        .filter((row) => !stateFilter || row.state === stateFilter),
    [apiKeys, stateFilter]
  );

  const {
    items,
    allItems,
    pagination,
    searchTerm,
    setSearchTerm,
    setPage,
    setPageSize,
  } = useMockPagination({ data: rows, searchFields: SEARCH_FIELDS });

  const openKeyModal = (apiKey?: IIntegrationApiKey) => {
    openModal(<IntegrationApiKeyModal apiKey={apiKey} onClose={closeModal} />, {
      width: "min(640px, 94vw)",
      maxHeight: "90vh",
      showCloseButton: true,
    });
  };

  const openRevokeModal = (apiKey: IIntegrationApiKey) => {
    openModal(
      <RevokeIntegrationApiKeyModal apiKey={apiKey} onClose={closeModal} />,
      { width: "min(520px, 94vw)", showCloseButton: true }
    );
  };

  const handleReactivate = async (apiKey: IIntegrationApiKey) => {
    try {
      await updateApiKey({
        id: apiKey.id,
        request: {
          description: apiKey.description,
          claims: apiKey.claims,
          expiresIn: apiKey.expiresIn,
          active: true,
        },
      });
      showToast("The API key was reactivated.", "success");
    } catch (error) {
      showToast(
        errorToastMessage(
          error,
          "There was an error reactivating this API key."
        ),
        "warning"
      );
    }
  };

  const columns: ColumnDef<IApiKeyRow, string>[] = useMemo(
    () => [
      {
        accessorKey: "keyPrefix",
        header: "Prefix",
        // TableSorted prints "-" for a null value unless the cell opts in;
        // a missing prefix is a fact about the key, shown as an em dash.
        meta: { renderEmpty: true },
        cell: ({ row }) =>
          row.original.keyPrefix ? (
            <KeyPrefix>{row.original.keyPrefix}…</KeyPrefix>
          ) : (
            <span>—</span>
          ),
        enableSorting: true,
      },
      {
        accessorKey: "description",
        header: "Description",
        cell: ({ row }) => (
          <TruncatedCell title={row.original.description}>
            {row.original.description}
          </TruncatedCell>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "region",
        header: "Region",
        cell: ({ row }) => <span>{row.original.region}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "claimsText",
        header: "Claims",
        meta: { renderEmpty: true },
        cell: ({ row }) =>
          row.original.claims.length > 0 ? (
            <ChipList>
              {row.original.claims.map((claim) => (
                <ClaimChip key={claim}>{claim}</ClaimChip>
              ))}
            </ChipList>
          ) : (
            <span>—</span>
          ),
        enableSorting: false,
      },
      {
        accessorKey: "expiresIn",
        header: "Expires",
        cell: ({ row }) => (
          <ExpiryCell>
            {getLongFormattedDate(row.original.expiresIn, "—")}
          </ExpiryCell>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "state",
        header: "State",
        cell: ({ row }) => (
          <StatusBadge status={stateTone(row.original.state)}>
            {row.original.state}
          </StatusBadge>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "created",
        header: "Created",
        cell: ({ row }) => (
          <span>
            {getLongFormattedDate(row.original.created, "—")}
            {row.original.createdBy ? ` · ${row.original.createdBy}` : ""}
          </span>
        ),
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActionGroup>
            <RowAction
              label="Edit key"
              icon={<Pencil1Icon />}
              onClick={() => openKeyModal(row.original)}
            />
            {row.original.active ? (
              <RowAction
                label="Revoke key"
                icon={<TrashIcon />}
                danger
                onClick={() => openRevokeModal(row.original)}
              />
            ) : (
              <RowAction
                label="Reactivate key"
                icon={<ResetIcon />}
                disabled={row.original.isExpired || isUpdatingApiKey}
                onClick={() => handleReactivate(row.original)}
              />
            )}
          </RowActionGroup>
        ),
      },
    ],
    [apiKeys, isUpdatingApiKey]
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageTitleGroup>
          <PageTitle>API keys</PageTitle>
          <PageInfoTooltip title="API keys">
            Machine credentials for the integration API, Retool and webhooks. A
            key value is shown once, when it is created, and cannot be retrieved
            afterwards — only its prefix is kept. Revoking keeps the row so the
            key can be reactivated; an expired key cannot.
          </PageInfoTooltip>
        </PageTitleGroup>
      </PageHeader>

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Search by prefix, description, region or claim..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <FilterGroup>
          <FilterLabel htmlFor="api-key-state-filter">State</FilterLabel>
          <AdminSelectField
            id="api-key-state-filter"
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value as TStateFilter)}
          >
            {STATE_OPTIONS.map((option) => (
              <option key={option.label} value={option.value}>
                {option.label}
              </option>
            ))}
          </AdminSelectField>
        </FilterGroup>
        <FiltersActions>
          <FilterButton
            variant="primary"
            onClick={() => openKeyModal()}
            data-testid="api-key-new"
          >
            New key
          </FilterButton>
        </FiltersActions>
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load the integration API keys. Try refreshing the page.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[[INTEGRATION_API_KEYS_QUERY_KEY]]}>
          <TableSorted
            columns={columns}
            data={items}
            emptyStateText={isLoading ? <TableLoadingState /> : "No rows found"}
          />
        </TableWrapper>
        <TablePagination
          exportCsv={{
            filename: "integration-api-keys",
            columns,
            rows: allItems,
          }}
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </TableContainer>
    </PageContainer>
  );
}
